/**
 * Shared presentational shell for the accountant tabs:
 * search input, status filter, table header, pagination, error banner.
 * Each tab wraps its row list in this.
 */
import React from 'react';
import { Search, X, AlertTriangle } from 'lucide-react';

export type StatusFilterValue = 'all' | 'ready' | 'stale' | 'needs-review';

interface Props {
  tabPanelId: string;
  tabPanelLabelledBy: string;

  searchQuery: string;
  onSearchChange: (v: string) => void;
  statusFilter: StatusFilterValue;
  onStatusFilterChange: (v: StatusFilterValue) => void;

  heading: string;
  filteredCount: number;
  totalCount: number;

  loading: boolean;
  error: string | null;
  onDismissError?: () => void;
  onRetry?: () => void;

  page: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;

  emptyMessage: string;
  isEmpty: boolean;
  children: React.ReactNode;
}

export default function TabShell({
  tabPanelId,
  tabPanelLabelledBy,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  heading,
  filteredCount,
  totalCount,
  loading,
  error,
  onDismissError,
  onRetry,
  page,
  totalPages,
  onPrevPage,
  onNextPage,
  emptyMessage,
  isEmpty,
  children,
}: Props) {
  return (
    <div>
      {error && (
        <div
          className="mb-4 p-3 border rounded-lg flex items-center gap-2"
          style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-sm">{error}</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-xs px-2 py-1 rounded font-medium flex-shrink-0"
              style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            >
              Retry
            </button>
          )}
          {onDismissError && (
            <button
              onClick={onDismissError}
              className="flex-shrink-0"
              style={{ color: 'var(--color-needs-human-text)' }}
              aria-label="Dismiss error"
            >
              ✕
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }} data-testid="queue-heading">
          {heading} ({filteredCount}
          {filteredCount !== totalCount ? ` of ${totalCount}` : ''})
        </h2>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
            <input
              type="text"
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-8 pr-8 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ borderColor: 'var(--color-border)', '--tw-ring-color': 'var(--color-brand)' } as React.CSSProperties}
              data-testid="search-input"
              aria-label="Search cases by name, client, law firm, or event type"
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--color-text-dim)' }}
                aria-label="Clear search"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as StatusFilterValue)}
            className="px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)', '--tw-ring-color': 'var(--color-brand)' } as React.CSSProperties}
            data-testid="status-filter"
            aria-label="Filter by case status"
          >
            <option value="all">All Status</option>
            <option value="ready">Ready for Sign-off</option>
            <option value="needs-review">Needs Review</option>
            <option value="stale">Stale (5+ days)</option>
          </select>
        </div>
      </div>

      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
        role="tabpanel"
        id={tabPanelId}
        aria-labelledby={tabPanelLabelledBy}
      >
        {loading ? (
          <div className="p-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: 'var(--color-brand)' }} />
            <p className="mt-3 text-sm">Loading cases...</p>
          </div>
        ) : isEmpty ? (
          <div className="p-8 text-center" style={{ color: 'var(--color-text-muted)' }}>{emptyMessage}</div>
        ) : (
          <>
            <div
              className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-label)' }}
            >
              <div className="col-span-3">Case</div>
              <div className="col-span-2">Event / Date</div>
              <div className="col-span-3">Status</div>
              <div className="col-span-1 text-center text-[10px] tracking-normal leading-tight">Strength</div>
              <div className="col-span-1 text-center text-[10px] tracking-normal leading-tight">Value</div>
              <div className="col-span-2 text-right">Quick Actions</div>
            </div>
            <div className="divide-y divide-[color:var(--color-border-faint)] max-h-[500px] overflow-y-auto">{children}</div>
          </>
        )}

        {totalPages > 1 && (
          <nav
            className="px-5 py-4 border-t flex items-center justify-between"
            style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
            aria-label="Queue pagination"
          >
            <button
              onClick={onPrevPage}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              aria-label="Go to previous page"
            >
              Previous
            </button>
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }} aria-live="polite">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={onNextPage}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              aria-label="Go to next page"
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </div>
  );
}
